import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getMyOrders } from "../utils/api";

function money(x) {
  const n = Number(x || 0);
  return n.toFixed(2);
}

export default function MyOrders({ token, currentUser }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setErr("");
      setLoading(true);

      if (!token) {
        setOrders([]);
        setLoading(false);
        return;
      }

      try {
        const data = await getMyOrders(token);
        const list = Array.isArray(data?.orders)
          ? data.orders
          : Array.isArray(data)
            ? data
            : [];
        if (!cancelled) setOrders(list);
      } catch (e) {
        if (!cancelled) setErr("Failed to load orders.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [token]);

  if (!token) {
    return (
      <div className="container pagePadBottom">
        <div className="card">
          <div style={{ fontWeight: 700 }}>You're not logged in</div>
          <div className="muted" style={{ marginTop: 6 }}>
            Log in to view your orders.
          </div>
          <div style={{ marginTop: 12 }}>
            <Link className="btnPrimary" to="/login">
              Go to Login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container pagePadBottom">
      <div className="pageHeader">
        <div className="pageHeaderLeft">
          <div className="h2">My Orders</div>
          <div className="muted small" style={{ marginTop: 6 }}>
            {currentUser?.name ? `Hi ${currentUser.name} - ` : ""}your order
            history.
          </div>
        </div>
        <div className="pageHeaderRight">
          <span className="pill">
            <span className="pillStrong">{orders.length}</span> orders
          </span>
        </div>
      </div>

      {err ? (
        <div className="alert alertDanger">
          <div className="alertTitle">Error</div>
          <div className="alertBody">{err}</div>
        </div>
      ) : null}

      {loading ? (
        <div className="emptyBox">
          <div className="emptyTitle">Loading…</div>
          <div className="muted small">Fetching your orders.</div>
        </div>
      ) : orders.length === 0 ? (
        <div className="emptyBox">
          <div className="emptyTitle">No orders yet</div>
          <div className="muted small">
            Browse the shop and check out when ready.
          </div>
          <div style={{ marginTop: 12 }}>
            <Link className="btnSecondary" to="/categories">
              Go to Shop
            </Link>
          </div>
        </div>
      ) : (
        <div style={{ display: "grid", gap: 12, marginTop: 14 }}>
          {orders.map((o) => {
            const id = o?.id ?? o?.order_id ?? o?.orderId;
            const status = o?.status || "pending";
            const created = o?.created_at ?? o?.createdAt ?? "";
            const total =
              o?.total_price ?? o?.total ?? o?.order_total ?? o?.amount ?? 0;
            const items = Array.isArray(o?.items) ? o.items : [];

            return (
              <Link
                key={String(id)}
                to={id ? `/orders/${id}` : "#"}
                className="card"
                style={{
                  display: "block",
                  textDecoration: "none",
                  color: "inherit",
                  cursor: id ? "pointer" : "not-allowed",
                }}
                aria-disabled={!id}
                onClick={(e) => {
                  if (!id) e.preventDefault();
                }}
                title={id ? "View order details" : "Missing order id"}
              >
                <div className="cardTitleRow">
                  <div className="cardTitle">Order #{id}</div>
                  <span className="badge">{String(status).toUpperCase()}</span>
                </div>

                <div className="muted small" style={{ marginTop: 6 }}>
                  {created ? `Placed: ${created}` : null}
                </div>

                <div className="divider" />

                {items.length ? (
                  <div style={{ display: "grid", gap: 8 }}>
                    {items.map((it, idx) => {
                      const name =
                        it?.item_name ?? it?.name ?? it?.product_name ?? "Item";
                      const qty = Number(it?.quantity ?? it?.qty ?? 1);
                      const unitPrice = Number(
                        it?.unit_price ?? it?.price ?? 0,
                      );
                      const lineTotal = unitPrice * qty;

                      return (
                        <div
                          key={idx}
                          className="row"
                          style={{ justifyContent: "space-between" }}
                        >
                          <div style={{ minWidth: 0 }}>
                            <div
                              style={{
                                fontWeight: 600,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {name}
                            </div>
                            <div className="muted small">Qty: {qty}</div>
                          </div>
                          <div className="priceBlue">${money(lineTotal)}</div>
                          <div className="muted small">
                            Qty: {qty}
                            {qty > 1 ? ` • Each: $${money(unitPrice)}` : ""}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="muted small">
                    Click to view details (items will load there).
                  </div>
                )}

                <div className="divider" />

                <div
                  className="row"
                  style={{ justifyContent: "space-between" }}
                >
                  <div style={{ fontWeight: 700 }}>Total</div>
                  <div className="priceBlue">${money(total)}</div>
                </div>

                <div className="muted small" style={{ marginTop: 10 }}>
                  View details →
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
